import React, { useMemo } from 'react'
import { StatsCard } from './stats/StatsCard'
import { DollarSign } from 'lucide-react'
import { formatMoney } from '@/utils'
import type { MonthlyAvgDataPoint } from '@/types/otc'
import { Area, AreaChart, ResponsiveContainer, YAxis } from 'recharts'

interface TotalValueCardProps {
  totalValue: number
  loading?: boolean
  monthlyAvg?: MonthlyAvgDataPoint[]
  label?: string
}

type TotalValueChartPoint = {
  i: number
  value: number
}

function mulberry32(seed: number) {
  let t = seed >>> 0
  return () => {
    t += 0x6d2b79f5
    let r = Math.imul(t ^ (t >>> 15), 1 | t)
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

const TotalValueChart: React.FC<{ monthlyAvg?: MonthlyAvgDataPoint[]; seed: number }> = ({
  monthlyAvg,
  seed,
}) => {
  const data = useMemo<TotalValueChartPoint[]>(() => {
    const points = (monthlyAvg ?? [])
      .filter((p) => typeof p?.value === 'number' && Number.isFinite(p.value))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    const slice = points.length > 0 ? points.slice(-8) : []

    if (slice.length >= 2) {
      const values = slice.map((p) => p.value)
      const min = Math.min(...values)
      const max = Math.max(...values)
      const range = max - min || 1
      return slice.map((p, idx) => ({
        i: idx,
        value: ((p.value - min) / range) * 100,
      }))
    }

    const fallbackShape = [8, 8, 42, 42, 24, 54, 54, 78]
    const rng = mulberry32(Math.max(1, Math.floor(seed)))
    const jitter = rng() * 6

    return fallbackShape.map((value, idx) => ({
      i: idx,
      value: Math.min(100, value + jitter),
    }))
  }, [monthlyAvg, seed])

  // Colors from Figma (37:1173 / 37:1180)
  const stroke = '#c8ed03'
  const fillTop = 'rgba(200,237,3,0.3)'
  const fillBottom = 'rgba(200,237,3,0)'

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="totalValueFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={fillTop} />
              <stop offset="100%" stopColor={fillBottom} />
            </linearGradient>
          </defs>
          <YAxis hide domain={[0, 100]} />
          <Area
            type="linear"
            dataKey="value"
            stroke={stroke}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#totalValueFill)"
            fillOpacity={1}
            dot={false}
            activeDot={false}
            isAnimationActive
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

/**
 * Screen: Secondaries trading
 * Total Value card extracted as a standalone component.
 * Must render exactly the same UI as the inline block in Secondaries metrics cards.
 */
export const TotalValueCard: React.FC<TotalValueCardProps> = ({
  totalValue,
  loading = false,
  monthlyAvg,
  label = 'Total Value',
}) => {
  return (
    <StatsCard
      icon={<DollarSign className="h-4 w-4" strokeWidth={1.8} />}
      iconAlt="Total Value"
      label={label}
      value={
        <>
          <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
          <span className="text-[#2d2923] dark:text-[#fdfdfd]">{formatMoney(totalValue).replace('$', '').trim()}</span>
        </>
      }
      loading={loading}
      mobileHeight="h-[126px]"
      chart={
        <TotalValueChart monthlyAvg={monthlyAvg} seed={totalValue} />
      }
    />
  )
}
